import type { ID } from "types";
import { HttpVerb } from "consts/api";
import { when } from "util/support";
import { blobDownload } from "util/download";
import { ApiStrategies, applyMiddleware, RefreshToken } from "./helpers";
import {
    FormSerializers,
    type Middleware,
    type DownloadableRequestInfo,
    type ExtendedRequestInfo,
    type UrlString
} from "./types";
import mw from "./middleware";

const fetchApi = async (
    url: UrlString,
    requestInfo?: ExtendedRequestInfo,
    middleware: Array<Middleware> = mw
) => {
    await RefreshToken.refresh()
        .catch(() => window.location.reload());

    const {
        baseUrl = process.env.REACT_APP_BACKEND_ORIGIN_URL,
        abortController = new AbortController(),
        signal = abortController.signal,
        serializer = FormSerializers.Json,
        getBody = ApiStrategies[serializer].getBody,
        getHeaders = ApiStrategies[serializer].getHeaders,
        ...initRequestInfo
    } = requestInfo ?? {} as ExtendedRequestInfo;

    const {
        url: href,
        ...restRequestInfo
    } = fetchApi.withMiddleware(middleware)(`${baseUrl}${url}`, {
        ...initRequestInfo,
        signal,
        baseUrl,
        abortController,
        serializer,
        getBody,
        getHeaders
    });

    return fetch(href, restRequestInfo as RequestInit);
};

fetchApi.withMiddleware = applyMiddleware;

type CreateOrUpdateResourceArgs<T> = {
    readonly body: Partial<T>;
    readonly getRoute: (id?: ID) => string;
    readonly getId?: (body: Partial<T>) => ID | undefined;
};

export function updateOrCreateResource<T>({
    body,
    getRoute,
    getId
}: CreateOrUpdateResourceArgs<T>) {
    const resourceId = getId?.(body);

    return fetchApi(getRoute(resourceId), {
        method: when(
            resourceId,
            HttpVerb.Put,
            HttpVerb.Post
        ),
        body,
        serializer: FormSerializers.Json
    });
};

export const downloadFileApi = async (
    url: string,
    { fileName, ...extendedRequestSettings }: DownloadableRequestInfo
) => {
    const response = await RefreshToken.refresh({ force: true })
        .catch(() => window.location.reload())
        .finally()
        .then(() => fetchApi(url, extendedRequestSettings));

    if (response.ok) {
        blobDownload({
            fileName,
            obj: (await response.clone().blob())
        });
    }

    return response;
};

export default fetchApi;
